import React, { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, useTheme } from "@mui/material";
import { ErrorBox, OrgPageHeader, Pager, Spinner } from "./components/organisation/OrgModal";
import { cachedActiveOrganisation, fetchAuditLog, formatOrgDate, orgErrorMessage } from "./utils/organisationApi";

// Every action has a name on it, including Super Admin support access (spec 0.3 and 6)
const AuditLogPage = () => {
  const theme = useTheme();
  const organisationName = cachedActiveOrganisation()?.name || "";
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [superAdminOnly, setSuperAdminOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setListError("");
    fetchAuditLog({ page: currentPage, limit: rowsPerPage, superAdminOnly })
      .then((result) => {
        if (cancelled) return;
        setEntries(result.entries);
        setTotalCount(result.count);
      })
      .catch((err) => {
        if (cancelled) return;
        setEntries([]);
        setTotalCount(0);
        setListError(orgErrorMessage(err, "Could not load the activity log."));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentPage, rowsPerPage, superAdminOnly]);

  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;
  const headerCellSx = {
    backgroundColor: theme.palette.secondary.v5,
    color: theme.palette.text.main,
    zIndex: 10,
    paddingLeft: 2,
  };
  const cellSx = { paddingLeft: 2, paddingRight: 2, verticalAlign: "top" };

  return (
    <div
      className="flex flex-col w-full h-full overflow-auto"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <OrgPageHeader title="Activity Log" subtitle={organisationName}>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={superAdminOnly}
            onChange={(e) => {
              setSuperAdminOnly(e.target.checked);
              setCurrentPage(1);
            }}
          />
          Show only Sift Hire support access
        </label>
      </OrgPageHeader>

      <p className="text-sm text-accent-gray mx-6 max-md:mx-4 mt-4">
        When Sift Hire support opens your organisation to help, every action is recorded here under their own name.
      </p>

      {isLoading ? (
        <Spinner label="Loading activity..." />
      ) : (
        <>
          <div
            className="overflow-auto border rounded-md mx-6 max-md:mx-4 mt-4"
            style={{ borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main }}
          >
            {listError && (
              <div className="m-4">
                <ErrorBox>{listError}</ErrorBox>
              </div>
            )}
            {entries.length === 0 ? (
              listError ? null : (
                <div className="flex flex-col items-center justify-center h-80 text-accent-gray">
                  <p className="text-lg font-medium mb-1">No activity yet</p>
                  <p className="text-sm max-w-md text-center px-6">
                    {superAdminOnly ? "Sift Hire support hasn't accessed this organisation." : "Actions taken in this organisation will show here."}
                  </p>
                </div>
              )
            ) : (
              <Table stickyHeader className="w-full min-w-max" sx={{ backgroundColor: theme.palette.primary.main }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>When</TableCell>
                    <TableCell sx={headerCellSx}>Who</TableCell>
                    <TableCell sx={headerCellSx}>What</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{formatOrgDate(entry.createdAt, true)}</TableCell>
                      <TableCell sx={cellSx}>
                        <div className="flex flex-col">
                          <span className="flex items-center gap-2">
                            {entry.actorName || "—"}
                            {entry.isSuperAdmin && (
                              <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-[#fff4d6] text-[#7a5300]">
                                Sift Hire support
                              </span>
                            )}
                          </span>
                          {entry.actorEmail && entry.actorEmail !== entry.actorName && (
                            <span className="text-sm text-accent-gray-light">{entry.actorEmail}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, maxWidth: 480, whiteSpace: "normal" }}>{entry.summary || entry.action || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {entries.length > 0 && (
            <Pager
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogPage;
